import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { NAP, BOOKING_URL } from "../lib/config";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#1A1A1A] text-[#FDFBF7] pt-16 pb-8 border-t-4 border-[#E85D26]"
    >
      <div className="wb-container grid grid-cols-1 md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <div className="inline-block bg-[#FDFBF7] border-l-4 border-[#E85D26] py-3 px-4 rounded-sm mb-4">
            <img
              src="/images/logo-horizontal.png"
              alt="Westbridge Inn & Suites"
              className="h-12 w-auto"
            />
          </div>
          <p
            className="text-lg font-bold text-[#E85D26]"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            {NAP.slogan}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-[#FDFBF7]/80">
            A bridge between the road and a good night's rest — in Clinton, Missouri.
          </p>
        </div>

        <div>
          <h4
            className="text-sm font-black uppercase tracking-[0.18em] text-[#E85D26] mb-4"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            Visit Us
          </h4>
          <address className="not-italic space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 mt-0.5 text-[#E85D26]" />
              <span>
                {NAP.addressLine1}
                <br />
                {NAP.city}, {NAP.state} {NAP.postal}
              </span>
            </div>
            <a
              href={`tel:${NAP.phoneTel}`}
              className="flex items-center gap-3 hover:text-[#E85D26]"
              data-testid="footer-phone-link"
            >
              <Phone className="h-4 w-4 text-[#E85D26]" /> {NAP.phone}
            </a>
            <a
              href={`mailto:${NAP.email}`}
              className="flex items-center gap-3 hover:text-[#E85D26]"
              data-testid="footer-email-link"
            >
              <Mail className="h-4 w-4 text-[#E85D26]" /> {NAP.email}
            </a>
          </address>
        </div>

        <div>
          <h4
            className="text-sm font-black uppercase tracking-[0.18em] text-[#E85D26] mb-4"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            Explore
          </h4>
          <ul className="space-y-2 text-sm">
            {[
              ["/rooms", "Rooms"],
              ["/amenities", "Amenities"],
              ["/gallery", "Gallery"],
              ["/local-guide", "Local Guide"],
              ["/blog", "Journal"],
              ["/faq", "FAQ"],
              ["/contact", "Contact"],
              ["/accessibility", "Accessibility"],
            ].map(([to, label]) => (
              <li key={to}>
                <Link
                  to={to}
                  className="hover:text-[#E85D26]"
                  data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4
            className="text-sm font-black uppercase tracking-[0.18em] text-[#E85D26] mb-4"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            Trips
          </h4>
          <ul className="space-y-2 text-sm">
            {[
              ["/truman-lake-fishing", "Truman Lake Fishing"],
              ["/boaters", "Boaters & Anglers"],
              ["/katy-trail-cyclists", "Katy Trail Cyclists"],
              ["/pet-friendly", "Pet Friendly"],
              ["/reunions", "Reunions & Festivals"],
              ["/extended-stay", "Work Crews & Truckers"],
              ["/lake-activities", "KC Weekenders"],
            ].map(([to, label]) => (
              <li key={to}>
                <Link to={to} className="hover:text-[#E85D26]" data-testid={`footer-audience-${to.slice(1)}`}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4
            className="text-sm font-black uppercase tracking-[0.18em] text-[#E85D26] mb-4"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            Ready to stay?
          </h4>
          <p className="text-sm text-[#FDFBF7]/80 mb-4">
            Check-in {NAP.checkIn} · Check-out {NAP.checkOut}. Kids under 18 stay free in existing beds.
          </p>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="wb-btn-primary w-full"
            data-testid="footer-book-now"
          >
            Book Now
          </a>
          <a
            href={`tel:${NAP.phoneTel}`}
            className="mt-3 wb-btn-outline w-full bg-transparent"
            data-testid="footer-call-now"
            style={{ borderColor: "#E85D26", color: "#E85D26" }}
          >
            <Phone className="h-4 w-4" /> Call the front desk
          </a>
        </div>
      </div>

      <div className="wb-container mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-[#FDFBF7]/60">
        <div>© {new Date().getFullYear()} Westbridge Inn &amp; Suites. Individually owned and operated in Clinton, MO.</div>
        <div className="flex gap-4">
          <Link to="/accessibility" className="hover:text-[#E85D26]">
            Accessibility
          </Link>
          <Link to="/faq" className="hover:text-[#E85D26]">
            FAQ
          </Link>
        </div>
      </div>
    </footer>
  );
}
